import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Mail, CheckCircle, Copy, Send } from 'lucide-react';
import { authAPI } from '../lib/api';

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.forgotPassword(email);
      setSubmitted(true);
      
      // Check if email was sent or if we're in demo mode
      if (response.data.email_sent) {
        setEmailSent(true);
        toast.success('Password reset email sent!');
      } else if (response.data.reset_token) {
        // Demo mode - show token
        setResetToken(response.data.reset_token);
        toast.success('Password reset instructions generated');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Failed to process request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(resetToken);
    toast.success('Token copied to clipboard');
  };

  if (submitted) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1E3A5F] mb-8 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>

          <div className="card-dashboard" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {emailSent ? <Send className="w-8 h-8 text-green-600" /> : <CheckCircle className="w-8 h-8 text-green-600" />}
              </div>
              <h1 className="text-2xl font-bold font-['Outfit'] text-[#1E3A5F] mb-2">
                {emailSent ? 'Email Sent!' : 'Check Your Email'}
              </h1>
              <p className="text-[#4A4A4A]">
                {emailSent 
                  ? <>We've sent password reset instructions to <strong>{email}</strong>. Check your inbox!</>
                  : <>If an account exists for <strong>{email}</strong>, you will receive password reset instructions.</>
                }
              </p>
            </div>

            {emailSent && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Didn't receive the email?</strong> Check your spam folder or request a new reset link.
                </p>
              </div>
            )}

            {resetToken && !emailSent && (
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-amber-800 font-medium mb-2">
                  Demo Mode: Use this reset token
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded text-sm font-mono break-all border">
                    {resetToken}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={copyToken}
                    className="shrink-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-amber-700 mt-2">
                  Email service not configured. Token expires in 1 hour.
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Link to={`/reset-password${resetToken ? `?token=${resetToken}` : ''}`}>
                <Button className="btn-primary w-full" data-testid="go-to-reset-btn">
                  Reset Password Now
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="w-full border-2 border-[#1E3A5F]">
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1E3A5F] mb-8 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>

        <div className="card-dashboard" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
          <div className="mb-8 text-center">
            <div className="w-16 h-16 bg-[#FFEDDF] rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-[#1E3A5F]" />
            </div>
            <h1 className="text-2xl font-bold font-['Outfit'] text-[#1E3A5F] mb-2">Forgot Password?</h1>
            <p className="text-[#4A4A4A]">
              No worries! Enter your email and we'll send you reset instructions.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-medium text-[#1E3A5F]">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-default w-full"
                data-testid="forgot-email-input"
              />
            </div>

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Instructions'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#4A4A4A]">
              Remember your password?{' '}
              <Link to="/login" className="text-[#FE5F55] font-semibold hover:underline">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
